import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { CalendarClock, Check, ClipboardList, Pencil, Plus } from "lucide-react";

import api from "../../../lib/api";
import { useAuth } from "../../../context/AuthContext";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { Input } from "../../../components/ui/Input";

interface ContactoOption {
  id: number;
  nombre: string;
  activo: boolean;
}

interface OportunidadOption {
  id: number;
  idExterno: string | null;
  titulo: string;
  estado: "abierta" | "ganada" | "perdida";
}

interface Actividad {
  id: number;
  idExterno: string | null;
  tipo: "llamada" | "reunion" | "email" | "nota" | "otro";
  asunto: string;
  detalle: string | null;
  realizadaAt: string;
  contactoId: number | null;
  contacto: string | null;
  oportunidadId: number | null;
  oportunidad: string | null;
  createdByUserId: number | null;
  autorNombre: string | null;
  autorEmail: string | null;
}

interface Tarea {
  id: number;
  idExterno: string | null;
  tipo: "tarea" | "llamada" | "reunion" | "email";
  titulo: string;
  detalle: string | null;
  venceAt: string;
  recordatorioAt: string | null;
  estado: "pendiente" | "completada" | "cancelada";
  prioridad: "baja" | "normal" | "alta";
  contactoId: number | null;
  contacto: string | null;
  oportunidadId: number | null;
  oportunidad: string | null;
  calendarSyncEstado: "no_solicitado" | "pendiente" | "sincronizado" | "error";
  calendarSyncError: string | null;
  responsableUserId: number | null;
}

interface SeguimientoResponse {
  actividades: Actividad[];
  tareas: Tarea[];
  oportunidades: OportunidadOption[];
}

interface CalendarStatus {
  configured: boolean;
  connected: boolean;
}

function getErrorMessage(
  error: unknown,
  fallback = "No se pudo completar la operación"
): string {
  if (axios.isAxiosError<{ error?: string }>(error)) {
    return error.response?.data?.error ?? fallback;
  }
  return fallback;
}

function currentLocalDateTime(): string {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat("es-ES", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
}

function toLocalDateTimeInput(value: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

const activityLabels: Record<Actividad["tipo"], string> = {
  llamada: "Llamada",
  reunion: "Reunión",
  email: "Email",
  nota: "Nota",
  otro: "Otra",
};

export function CrmEmpresaSeguimiento({
  empresaId,
  contactos,
}: {
  empresaId: number;
  contactos: ContactoOption[];
}) {
  const { user } = useAuth();
  const [actividades, setActividades] = useState<Actividad[]>([]);
  const [tareas, setTareas] = useState<Tarea[]>([]);
  const [oportunidades, setOportunidades] = useState<OportunidadOption[]>([]);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [editingActivityId, setEditingActivityId] = useState<number | null>(null);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [updatingTaskId, setUpdatingTaskId] = useState<number | null>(null);
  const [activityForm, setActivityForm] = useState({
    tipo: "llamada" as Actividad["tipo"],
    asunto: "",
    detalle: "",
    realizadaAt: currentLocalDateTime(),
    contactoId: "",
    oportunidadId: "",
  });
  const [taskForm, setTaskForm] = useState({
    tipo: "tarea" as Tarea["tipo"],
    titulo: "",
    detalle: "",
    venceAt: "",
    prioridad: "normal" as Tarea["prioridad"],
    contactoId: "",
    oportunidadId: "",
    recordatorioAt: "",
    syncCalendar: false,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [trackingResponse, calendarResponse] = await Promise.all([
        api.get<SeguimientoResponse>(
          `/admin/crm/seguimiento/empresa/${empresaId}`
        ),
        api.get<CalendarStatus>("/admin/crm/calendar/status"),
      ]);
      setActividades(trackingResponse.data.actividades);
      setTareas(trackingResponse.data.tareas);
      setOportunidades(trackingResponse.data.oportunidades);
      setCalendarConnected(calendarResponse.data.connected);
    } catch (loadError) {
      setError(getErrorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  useEffect(() => {
    void load();
  }, [load]);

  const resetActivityForm = () => {
    setActivityForm({
      tipo: "llamada",
      asunto: "",
      detalle: "",
      realizadaAt: currentLocalDateTime(),
      contactoId: "",
      oportunidadId: "",
    });
    setEditingActivityId(null);
  };

  const closeActivityForm = () => {
    setShowActivityForm(false);
    resetActivityForm();
  };

  const openNewActivityForm = () => {
    resetActivityForm();
    setShowTaskForm(false);
    setShowActivityForm(true);
  };

  const openEditActivityForm = (actividad: Actividad) => {
    setActivityForm({
      tipo: actividad.tipo,
      asunto: actividad.asunto,
      detalle: actividad.detalle ?? "",
      realizadaAt: toLocalDateTimeInput(actividad.realizadaAt),
      contactoId: actividad.contactoId ? String(actividad.contactoId) : "",
      oportunidadId: actividad.oportunidadId
        ? String(actividad.oportunidadId)
        : "",
    });
    setEditingActivityId(actividad.id);
    setShowTaskForm(false);
    setShowActivityForm(true);
    window.setTimeout(() => {
      document
        .getElementById(`crm-activity-form-${empresaId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 0);
  };

  const saveActivity = async () => {
    setError("");
    if (!activityForm.asunto.trim() || !activityForm.realizadaAt) {
      setError("Indica el resumen y la fecha de la actividad.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        tipo: activityForm.tipo,
        asunto: activityForm.asunto,
        detalle: activityForm.detalle || null,
        realizada_at: new Date(activityForm.realizadaAt).toISOString(),
        contacto_id: activityForm.contactoId
          ? Number(activityForm.contactoId)
          : null,
        oportunidad_id: activityForm.oportunidadId
          ? Number(activityForm.oportunidadId)
          : null,
      };
      if (editingActivityId === null) {
        await api.post(
          `/admin/crm/seguimiento/actividades/empresa/${empresaId}`,
          payload
        );
      } else {
        await api.patch(
          `/admin/crm/seguimiento/actividades/${editingActivityId}`,
          payload
        );
      }
      closeActivityForm();
      await load();
      window.dispatchEvent(new Event("crm:seguimiento-actualizado"));
    } catch (saveError) {
      setError(getErrorMessage(saveError));
    } finally {
      setSaving(false);
    }
  };

  const resetTaskForm = () => {
    setTaskForm({
      tipo: "tarea",
      titulo: "",
      detalle: "",
      venceAt: "",
      prioridad: "normal",
      contactoId: "",
      oportunidadId: "",
      recordatorioAt: "",
      syncCalendar: false,
    });
    setEditingTaskId(null);
  };

  const closeTaskForm = () => {
    setShowTaskForm(false);
    resetTaskForm();
  };

  const openNewTaskForm = () => {
    resetTaskForm();
    setShowActivityForm(false);
    setShowTaskForm(true);
  };

  const openEditTaskForm = (tarea: Tarea) => {
    setTaskForm({
      tipo: tarea.tipo,
      titulo: tarea.titulo,
      detalle: tarea.detalle ?? "",
      venceAt: toLocalDateTimeInput(tarea.venceAt),
      prioridad: tarea.prioridad,
      contactoId: tarea.contactoId ? String(tarea.contactoId) : "",
      oportunidadId: tarea.oportunidadId ? String(tarea.oportunidadId) : "",
      recordatorioAt: toLocalDateTimeInput(tarea.recordatorioAt ?? ""),
      syncCalendar:
        tarea.calendarSyncEstado === "sincronizado" &&
        tarea.responsableUserId === user?.id,
    });
    setEditingTaskId(tarea.id);
    setShowActivityForm(false);
    setShowTaskForm(true);
    window.setTimeout(() => {
      document
        .getElementById(`crm-task-form-${empresaId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 0);
  };

  const saveTask = async () => {
    setError("");
    if (!taskForm.titulo.trim() || !taskForm.venceAt) {
      setError("Indica el título y la fecha de la próxima acción.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        tipo: taskForm.tipo,
        titulo: taskForm.titulo,
        detalle: taskForm.detalle || null,
        vence_at: new Date(taskForm.venceAt).toISOString(),
        recordatorio_at: taskForm.recordatorioAt
          ? new Date(taskForm.recordatorioAt).toISOString()
          : null,
        prioridad: taskForm.prioridad,
        contacto_id: taskForm.contactoId ? Number(taskForm.contactoId) : null,
        oportunidad_id: taskForm.oportunidadId
          ? Number(taskForm.oportunidadId)
          : null,
      };
      const taskId = editingTaskId;
      const response = taskId === null
        ? await api.post<{ id: number }>(
            `/admin/crm/seguimiento/tareas/empresa/${empresaId}`,
            payload
          )
        : await api.patch<{ id: number }>(
            `/admin/crm/seguimiento/tareas/${taskId}`,
            payload
          );
      let calendarError = "";
      if (taskForm.syncCalendar) {
        try {
          await api.post(`/admin/crm/calendar/tasks/${response.data.id}/sync`);
        } catch (syncError) {
          calendarError = getErrorMessage(
            syncError,
            "No se pudo añadir la tarea a Google Calendar"
          );
        }
      }
      closeTaskForm();
      await load();
      window.dispatchEvent(new Event("crm:seguimiento-actualizado"));
      if (calendarError) {
        setError(`La tarea se ha guardado en el CRM, pero ${calendarError.toLowerCase()}.`);
      }
    } catch (saveError) {
      setError(getErrorMessage(saveError));
    } finally {
      setSaving(false);
    }
  };

  const changeTaskState = async (
    tarea: Tarea,
    estado: Tarea["estado"]
  ) => {
    setUpdatingTaskId(tarea.id);
    setError("");
    try {
      await api.patch(`/admin/crm/seguimiento/tareas/${tarea.id}`, { estado });
      await load();
    } catch (updateError) {
      setError(getErrorMessage(updateError));
    } finally {
      setUpdatingTaskId(null);
    }
  };

  const activeContacts = contactos.filter((contacto) => contacto.activo);
  const pendingTasks = tareas.filter((tarea) => tarea.estado === "pendiente");
  const closedTasks = tareas.filter((tarea) => tarea.estado !== "pendiente");

  const RelationFields = ({
    contactoId,
    oportunidadId,
    onContactoChange,
    onOportunidadChange,
  }: {
    contactoId: string;
    oportunidadId: string;
    onContactoChange: (value: string) => void;
    onOportunidadChange: (value: string) => void;
  }) => (
    <>
      <label className="block">
        <span className="mb-1 block text-sm font-medium text-gray-700">
          Contacto relacionado
        </span>
        <select
          value={contactoId}
          onChange={(event) => onContactoChange(event.target.value)}
          className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
        >
          <option value="">Sin contacto concreto</option>
          {activeContacts.map((contacto) => (
            <option key={contacto.id} value={contacto.id}>
              {contacto.nombre}
            </option>
          ))}
        </select>
      </label>
      <label className="block">
        <span className="mb-1 block text-sm font-medium text-gray-700">
          Oportunidad relacionada
        </span>
        <select
          value={oportunidadId}
          onChange={(event) => onOportunidadChange(event.target.value)}
          className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
        >
          <option value="">Sin oportunidad concreta</option>
          {oportunidades.map((oportunidad) => (
            <option key={oportunidad.id} value={oportunidad.id}>
              {oportunidad.idExterno} · {oportunidad.titulo}
            </option>
          ))}
        </select>
      </label>
    </>
  );

  return (
    <section className="space-y-5 border-t border-gray-200 pt-5">
      <div>
        <h3 className="font-semibold text-gray-900">Seguimiento comercial</h3>
        <p className="mt-1 text-sm text-gray-600">
          Historial de actividad y próximas acciones de esta empresa.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="space-y-3 rounded-lg border border-gray-200 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h4 className="font-medium text-gray-900">Actividad</h4>
              <p className="text-sm text-gray-500">Llamadas, reuniones y notas.</p>
            </div>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                if (showActivityForm && editingActivityId === null) {
                  closeActivityForm();
                } else {
                  openNewActivityForm();
                }
              }}
            >
              <Plus className="h-4 w-4" /> Registrar
            </Button>
          </div>

          {showActivityForm && (
            <div
              id={`crm-activity-form-${empresaId}`}
              className="space-y-3 rounded-lg bg-gray-50 p-3"
            >
              <div className="text-sm font-semibold text-gray-900">
                {editingActivityId === null
                  ? "Registrar actividad"
                  : "Editar actividad"}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-gray-700">
                    Tipo
                  </span>
                  <select
                    value={activityForm.tipo}
                    onChange={(event) =>
                      setActivityForm((current) => ({
                        ...current,
                        tipo: event.target.value as Actividad["tipo"],
                      }))
                    }
                    className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                  >
                    {Object.entries(activityLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </label>
                <Input
                  label="Fecha y hora"
                  type="datetime-local"
                  value={activityForm.realizadaAt}
                  onChange={(event) =>
                    setActivityForm((current) => ({
                      ...current,
                      realizadaAt: event.target.value,
                    }))
                  }
                />
                <div className="sm:col-span-2">
                  <Input
                    label="Resumen *"
                    placeholder="Ej.: Llamada sobre propuesta de RC"
                    value={activityForm.asunto}
                    onChange={(event) =>
                      setActivityForm((current) => ({
                        ...current,
                        asunto: event.target.value,
                      }))
                    }
                  />
                </div>
                <RelationFields
                  contactoId={activityForm.contactoId}
                  oportunidadId={activityForm.oportunidadId}
                  onContactoChange={(value) =>
                    setActivityForm((current) => ({ ...current, contactoId: value }))
                  }
                  onOportunidadChange={(value) =>
                    setActivityForm((current) => ({ ...current, oportunidadId: value }))
                  }
                />
                <div className="sm:col-span-2">
                  <Input
                    label="Detalle"
                    value={activityForm.detalle}
                    onChange={(event) =>
                      setActivityForm((current) => ({
                        ...current,
                        detalle: event.target.value,
                      }))
                    }
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="secondary" onClick={closeActivityForm}>
                  Cancelar
                </Button>
                <Button size="sm" onClick={saveActivity} loading={saving}>
                  {editingActivityId === null
                    ? "Guardar actividad"
                    : "Guardar cambios"}
                </Button>
              </div>
            </div>
          )}

          {loading ? (
            <p className="py-4 text-center text-sm text-gray-500">Cargando…</p>
          ) : actividades.length === 0 ? (
            <p className="rounded-lg bg-gray-50 py-6 text-center text-sm text-gray-500">
              Sin actividad registrada.
            </p>
          ) : (
            <div className="max-h-80 space-y-2 overflow-y-auto">
              {actividades.map((actividad) => (
                <div key={actividad.id} className="rounded-lg border border-gray-200 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="neutral">{activityLabels[actividad.tipo]}</Badge>
                      <span className="font-medium text-gray-900">{actividad.asunto}</span>
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => openEditActivityForm(actividad)}
                      aria-label={`Editar actividad ${actividad.asunto}`}
                    >
                      <Pencil className="h-4 w-4" /> Editar
                    </Button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {actividad.idExterno} · {formatDateTime(actividad.realizadaAt)}
                    {actividad.contacto ? ` · ${actividad.contacto}` : ""}
                    {actividad.autorNombre || actividad.autorEmail
                      ? ` · ${actividad.autorNombre || actividad.autorEmail}`
                      : ""}
                  </p>
                  {actividad.detalle && (
                    <p className="mt-2 text-sm text-gray-600">{actividad.detalle}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-3 rounded-lg border border-gray-200 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h4 className="font-medium text-gray-900">Próximas acciones</h4>
              <p className="text-sm text-gray-500">Tareas y seguimientos pendientes.</p>
            </div>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                if (showTaskForm && editingTaskId === null) {
                  closeTaskForm();
                } else {
                  openNewTaskForm();
                }
              }}
            >
              <Plus className="h-4 w-4" /> Nueva tarea
            </Button>
          </div>

          {showTaskForm && (
            <div
              id={`crm-task-form-${empresaId}`}
              className="space-y-3 rounded-lg bg-gray-50 p-3"
            >
              <div className="text-sm font-semibold text-gray-900">
                {editingTaskId === null ? "Nueva tarea" : "Editar tarea"}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block sm:col-span-2">
                  <span className="mb-1 block text-sm font-medium text-gray-700">
                    Tipo de próxima acción
                  </span>
                  <select
                    value={taskForm.tipo}
                    onChange={(event) =>
                      setTaskForm((current) => ({
                        ...current,
                        tipo: event.target.value as Tarea["tipo"],
                      }))
                    }
                    className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                  >
                    <option value="tarea">Tarea general</option>
                    <option value="llamada">Llamada</option>
                    <option value="reunion">Reunión</option>
                    <option value="email">Email</option>
                  </select>
                </label>
                <div className="sm:col-span-2">
                  <Input
                    label="Próxima acción *"
                    placeholder="Ej.: Llamar para hacer seguimiento"
                    value={taskForm.titulo}
                    onChange={(event) =>
                      setTaskForm((current) => ({
                        ...current,
                        titulo: event.target.value,
                      }))
                    }
                  />
                </div>
                <Input
                  label="Fecha y hora *"
                  type="datetime-local"
                  value={taskForm.venceAt}
                  onChange={(event) =>
                    setTaskForm((current) => ({
                      ...current,
                      venceAt: event.target.value,
                    }))
                  }
                />
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-gray-700">
                    Prioridad
                  </span>
                  <select
                    value={taskForm.prioridad}
                    onChange={(event) =>
                      setTaskForm((current) => ({
                        ...current,
                        prioridad: event.target.value as Tarea["prioridad"],
                      }))
                    }
                    className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                  >
                    <option value="baja">Baja</option>
                    <option value="normal">Normal</option>
                    <option value="alta">Alta</option>
                  </select>
                </label>
                <RelationFields
                  contactoId={taskForm.contactoId}
                  oportunidadId={taskForm.oportunidadId}
                  onContactoChange={(value) =>
                    setTaskForm((current) => ({ ...current, contactoId: value }))
                  }
                  onOportunidadChange={(value) =>
                    setTaskForm((current) => ({ ...current, oportunidadId: value }))
                  }
                />
                <div className="sm:col-span-2">
                  <Input
                    label="Aviso previo (opcional)"
                    type="datetime-local"
                    value={taskForm.recordatorioAt}
                    onChange={(event) =>
                      setTaskForm((current) => ({
                        ...current,
                        recordatorioAt: event.target.value,
                      }))
                    }
                  />
                </div>
                <div className="sm:col-span-2">
                  <Input
                    label="Detalle"
                    value={taskForm.detalle}
                    onChange={(event) =>
                      setTaskForm((current) => ({
                        ...current,
                        detalle: event.target.value,
                      }))
                    }
                  />
                </div>
                {calendarConnected &&
                  (editingTaskId === null ||
                    tareas.find((tarea) => tarea.id === editingTaskId)
                      ?.responsableUserId === user?.id) && (
                  <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3 sm:col-span-2">
                    <input
                      type="checkbox"
                      checked={taskForm.syncCalendar}
                      onChange={(event) =>
                        setTaskForm((current) => ({
                          ...current,
                          syncCalendar: event.target.checked,
                        }))
                      }
                      className="mt-1 h-4 w-4"
                    />
                    <span>
                      <span className="block text-sm font-medium text-blue-900">
                        {editingTaskId === null
                          ? "Añadir también a Google Calendar"
                          : "Actualizar también en Google Calendar"}
                      </span>
                      <span className="block text-xs text-blue-700">
                        La tarea se guarda primero en el CRM; si Google falla, no se pierde.
                      </span>
                    </span>
                  </label>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="secondary" onClick={closeTaskForm}>
                  Cancelar
                </Button>
                <Button size="sm" onClick={saveTask} loading={saving}>
                  {editingTaskId === null ? "Crear tarea" : "Guardar cambios"}
                </Button>
              </div>
            </div>
          )}

          {pendingTasks.length === 0 ? (
            <p className="rounded-lg bg-gray-50 py-6 text-center text-sm text-gray-500">
              No hay acciones pendientes.
            </p>
          ) : (
            <div className="space-y-2">
              {pendingTasks.map((tarea) => (
                <div key={tarea.id} className="rounded-lg border border-gray-200 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <CalendarClock className="h-4 w-4 text-[#1A3AB8]" />
                        <Badge variant="neutral">
                          {tarea.tipo === "llamada"
                            ? "Llamada"
                            : tarea.tipo === "reunion"
                              ? "Reunión"
                              : tarea.tipo === "email"
                                ? "Email"
                                : "Tarea"}
                        </Badge>
                        <span className="font-medium text-gray-900">{tarea.titulo}</span>
                        {tarea.prioridad === "alta" && (
                          <Badge variant="danger">Alta</Badge>
                        )}
                        {tarea.calendarSyncEstado === "sincronizado" && (
                          <Badge variant="success">En Google Calendar</Badge>
                        )}
                        {tarea.calendarSyncEstado === "error" && (
                          <span title={tarea.calendarSyncError ?? undefined}>
                            <Badge variant="danger">Error de calendario</Badge>
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        {tarea.idExterno} · {formatDateTime(tarea.venceAt)}
                        {tarea.contacto ? ` · ${tarea.contacto}` : ""}
                        {user?.crmSupervisor && tarea.responsableUserId !== user.id
                          ? " · Agenda supervisada"
                          : ""}
                      </p>
                      {tarea.detalle && (
                        <p className="mt-2 text-sm text-gray-600">{tarea.detalle}</p>
                      )}
                    </div>
                    <div className="flex shrink-0 flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={updatingTaskId !== null}
                        onClick={() => openEditTaskForm(tarea)}
                      >
                        <Pencil className="h-4 w-4" /> Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={updatingTaskId !== null}
                        onClick={() => changeTaskState(tarea, "completada")}
                      >
                        <Check className="h-4 w-4" /> Completar
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {closedTasks.length > 0 && (
            <details>
              <summary className="cursor-pointer text-sm text-gray-600">
                Ver acciones cerradas ({closedTasks.length})
              </summary>
              <div className="mt-2 space-y-2">
                {closedTasks.map((tarea) => (
                  <div key={tarea.id} className="flex items-center justify-between gap-3 rounded-lg bg-gray-50 p-3 text-sm text-gray-600">
                    <span className="inline-flex items-center gap-2">
                      <ClipboardList className="h-4 w-4" /> {tarea.titulo}
                    </span>
                    <div className="flex shrink-0 flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={updatingTaskId !== null}
                        onClick={() => openEditTaskForm(tarea)}
                      >
                        <Pencil className="h-4 w-4" /> Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={updatingTaskId !== null}
                        onClick={() => changeTaskState(tarea, "pendiente")}
                      >
                        Reabrir
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      </div>
    </section>
  );
}
